import { FC, useEffect, useState } from "react";
import "./Login.scss";
import { useAuth } from "../../providers/AuthProvider";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useStyle } from "../../providers/StyleProvider";
import { Card, Col, Container, Row } from "react-bootstrap";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as BeviAjudaLogo } from "../../assets/img/bevi-ajuda-white.svg";
import { UserRoleEnum } from "../../enums/User/UserRoleEnum";
import { useSidebarFilter } from "../../providers/SidebarFilterProvider";
import { useUsers } from "../../providers/Access/Users/UsersProvider";
import useIsMobileApp from "../../hooks/useIsMobileApp";
import { getPostLoginPath } from "../../utils/MobileChatRedirect";

interface LoginProps { }

interface Inputs {
  username: string;
  password: string;
}

const usePathname = (): string => {
  const location = useLocation();
  return location.pathname;
};

const Login: FC<LoginProps> = () => {
  const [, setFocusedUsername] = useState<boolean>(false);
  const [, setFocusedPassword] = useState<boolean>(false);
  const { handleStatusTutorial } = useUsers();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  const { login, error, setError, onInvalid, isLoading, setIsLoading } =
    useAuth();
  const { isLoading: isLoadingStyle, style } = useStyle();
  const { setShowSidebarFilter } = useSidebarFilter();

  const navigate = useNavigate();
  const pathname = usePathname();
  const isMobile = useIsMobileApp();

  const handleLogin = async (data: any) => {
    setError("");
    setIsLoading(false);
    const logged = await login(data);
    if (logged.success) {
      handleStatusTutorial();
      navigate(
        getPostLoginPath(
          logged?.user,
          isMobile,
          logged?.user?.role === UserRoleEnum.ADMIN ? "/home" : "/",
        ),
      );
    }
  };

  useEffect(() => {
    pathname !== "/login" && navigate("/login");
  }, [pathname, navigate]);

  useEffect(() => {
    setShowSidebarFilter(false);
    if (process.env.REACT_APP_ENV === "production") {
      navigate("/login-notice");
    }
  }, []);

  return (
    <main
      className="Login main-content main-content-bg mt-0 h-100"
      data-testid="Login"
    >
      {!isLoadingStyle && style && (
        <>
          <div className="page-header min-vh-100">
            <Container>
              <Row>
                <Col
                  xl={4}
                  lg={5}
                  md={7}
                  className="d-flex flex-column mx-lg-0 mx-auto"
                >
                  <Card className="card-plain">
                    <Card.Header className="pb-0 text-start">
                      <h4 className="font-weight-bolder">Entrar</h4>
                      <p className="mb-0">
                        Informe seu login e senha para entrar
                      </p>
                    </Card.Header>
                    <Card.Body className="bg-white">
                      <form onSubmit={handleSubmit(handleLogin)}>
                        <div className="mb-3">
                          <input
                            {...register("username", {
                              required: true,
                              maxLength: 100,
                            })}
                            type="text"
                            className={`form-control form-control-lg ${onInvalid(
                              errors?.username,
                            )}`}
                            placeholder="Login"
                            aria-label="Login"
                            onFocus={() => setFocusedUsername(true)}
                            onBlur={() => setFocusedUsername(true)}
                          />
                          <ErrorMessage
                            name="Login"
                            type={errors?.username?.type}
                            max={25}
                          />
                        </div>
                        <div className="mb-3">
                          <input
                            {...register("password", {
                              required: true,
                              minLength: 6,
                            })}
                            type="password"
                            className={`form-control form-control-lg ${onInvalid(
                              errors?.password,
                            )}`}
                            placeholder="Senha"
                            aria-label="Senha"
                            onFocus={() => setFocusedPassword(true)}
                            onBlur={() => setFocusedPassword(true)}
                          />
                          <ErrorMessage
                            name="Senha"
                            type={errors?.password?.type}
                            min={6}
                          />
                        </div>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="rememberMe"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="rememberMe"
                          >
                            Lembrar-me
                          </label>
                        </div>
                        <div className="text-center">
                          <button
                            type="submit"
                            className="btn btn-lg btn-primary btn-lg w-100 mt-4 mb-0"
                            disabled={isLoading}
                          >
                            {!isLoading ? (
                              "Entrar"
                            ) : (
                              <FontAwesomeIcon icon={faSpinner} spin />
                            )}
                          </button>
                        </div>

                        {error && (
                          <label className="text-center text-danger error-login mt-3">
                            {error}
                          </label>
                        )}
                      </form>
                    </Card.Body>
                    <div className="card-footer text-center bg-white pt-0 px-lg-2 px-1">
                      <p className="mb-1 text-sm mx-auto">
                        <Link
                          to={`/forgotPassword`}
                          type="butto"
                          className="text-primary text-gradient font-weight-bold"
                        >
                          Esqueci minha senha
                        </Link>
                      </p>
                    </div>
                  </Card>
                </Col>
                <div className="col-6 d-lg-flex d-none h-100 my-auto pe-0 position-absolute top-0 end-0 text-center justify-content-center flex-column">
                  <div
                    className="position-relative bg-gradient-primary h-100 m-3 px-7 border-radius-lg d-flex flex-column justify-content-center overflow-hidden"
                    style={{
                      backgroundImage:
                        "url(https://images.unsplash.com/photo-1506748686214-e9df14d4d9d0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwzNjUyOXwwfDF8c2VhcmNofDJ8fGdyYWRpZW50JTIwcGxheWVyfGVufDB8fHx8MTY5MjE1NTQ3Ng&ixlib=rb-4.0.3&q=80&w=1080)",
                    }}
                  >
                    <span className="mask bg-gradient-primary opacity-6"></span>
                    <BeviAjudaLogo className="img-responsive w-100" />
                    <h4 className="mt-3 text-white font-weight-bolder position-relative">
                      Bem-vindo ao BeviAjuda
                    </h4>
                    <p className="text-white position-relative">
                      Conte com nossa assistente virtual para esclarecer dúvidas
                      sobre produtos, processos e serviços da Bevi. Com
                      tecnologia de ponta e acesso direto à nossa base de
                      conhecimento, você recebe respostas rápidas, precisas e
                      personalizadas.
                    </p>
                  </div>
                </div>
              </Row>
            </Container>
          </div>

          <div></div>
        </>
      )}
    </main>
  );
};

export default Login;
